import type {
  ALERT_MODE,
  ALERT_VIEW,
  SIDEBAR_VIEWS,
  StaticToastOptions,
  STATIC_TOAST_MODE,
  UIAction,
  UIContextType,
  UIState,
} from "@types";
import React, { FC, ReactNode, useCallback, useMemo } from "react";

const initialState: UIContextType = {
  displaySidebar: false,
  displayDropdown: false,
  displayAlert: false,
  displayStaticToast: false,

  alertView: {
    view: "ALERT",
    alertContent: [],
  },
  staticToastView: {
    mode: "success",
    textBody: "",
  },
  sidebarView: "MOBILE_MENU",
  closeDropdown: () => {},
  openDropdown: () => {},
  closeSidebar: () => {},
  openSidebar: () => {},
  toggleSidebar: () => {},
  closeSidebarIfPresent: () => {},
  setSidebarView: () => {},
  setAlertView: () => {},
  openAlert: () => {},
  closeAlert: () => {},

  openStaticToast() {},
  closeStaticToast() {},
  setStaticToastView() {},
};

export const UIContext = React.createContext<UIContextType>(initialState);

UIContext.displayName = "UIContext";

function uiReducer(state: UIState, action: UIAction): UIState {
  switch (action.type) {
    case "OPEN_SIDEBAR": {
      return {
        ...state,
        displaySidebar: true,
      };
    }
    case "CLOSE_SIDEBAR": {
      return {
        ...state,
        displaySidebar: false,
      };
    }
    case "OPEN_DROPDOWN": {
      return {
        ...state,
        displayDropdown: true,
      };
    }
    case "CLOSE_DROPDOWN": {
      return {
        ...state,
        displayDropdown: false,
      };
    }

    case "SET_SIDEBAR_VIEW": {
      return {
        ...state,
        sidebarView: action.view,
      };
    }
    case "SET_ALERT_VIEW":
      return {
        ...state,
        alertView: {
          view: action.view,
          alertContent: action.alertContent,
        },
      };
    case "OPEN_ALERT": {
      return {
        ...state,
        displayAlert: true,
        displaySidebar: false,
      };
    }
    case "CLOSE_ALERT": {
      return {
        ...state,
        displayAlert: false,
      };
    }
    case "OPEN_STATIC_TOAST": {
      return {
        ...state,
        displayAlert: false,
        displaySidebar: false,
        displayStaticToast: true,
      };
    }
    case "CLOSE_STATIC_TOAST": {
      return {
        ...state,
        displayStaticToast: false,
      };
    }
    case "SET_STATIC_TOAST_VIEW": {
      return {
        ...state,
        staticToastView: {
          ...action.payload,
        },
      };
    }
  }
}

export const UIProvider: FC<{ children?: ReactNode }> = (props) => {
  const [state, dispatch] = React.useReducer(uiReducer, initialState);

  const openSidebar = useCallback(() => dispatch({ type: "OPEN_SIDEBAR" }), []);
  const closeSidebar = useCallback(
    () => dispatch({ type: "CLOSE_SIDEBAR" }),
    []
  );
  const toggleSidebar = useCallback(
    () =>
      state.displaySidebar
        ? dispatch({ type: "CLOSE_SIDEBAR" })
        : dispatch({ type: "OPEN_SIDEBAR" }),
    [state.displaySidebar]
  );
  const closeSidebarIfPresent = useCallback(
    () => state.displaySidebar && dispatch({ type: "CLOSE_SIDEBAR" }),
    [state.displaySidebar]
  );

  const openDropdown = useCallback(
    () => dispatch({ type: "OPEN_DROPDOWN" }),
    []
  );
  const closeDropdown = useCallback(
    () => dispatch({ type: "CLOSE_DROPDOWN" }),
    []
  );

  const openStaticToast = useCallback(
    () => dispatch({ type: "OPEN_STATIC_TOAST" }),
    []
  );
  const closeStaticToast = useCallback(
    () => dispatch({ type: "CLOSE_STATIC_TOAST" }),
    []
  );

  const setStaticToastView = useCallback(
    (mode: STATIC_TOAST_MODE, textBody: string, options?: StaticToastOptions) =>
      dispatch({
        type: "SET_STATIC_TOAST_VIEW",
        payload: { mode, textBody, options },
      }),
    []
  );
  const setSidebarView = useCallback(
    (view: SIDEBAR_VIEWS) => dispatch({ type: "SET_SIDEBAR_VIEW", view }),
    []
  );

  const setAlertView = useCallback(
    (
      view: ALERT_VIEW,
      alertContent: { mode: ALERT_MODE; textBody: string }[]
    ) =>
      dispatch({
        type: "SET_ALERT_VIEW",
        view,
        alertContent,
      }),
    []
  );

  const openAlert = useCallback(() => dispatch({ type: "OPEN_ALERT" }), []);
  const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), []);

  const value = useMemo(
    () => ({
      ...state,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      closeSidebarIfPresent,
      openDropdown,
      closeDropdown,
      setSidebarView,
      setAlertView,
      openAlert,
      closeAlert,
      openStaticToast,
      closeStaticToast,
      setStaticToastView,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, closeSidebarIfPresent, toggleSidebar]
  );

  return <UIContext.Provider value={value} {...props} />;
};

export const useUI = () => {
  const context = React.useContext(UIContext);
  if (context === undefined) {
    throw new Error(`useUI must be used within a UIProvider`);
  }
  return context;
};

export const ManagedUIContext: FC<{ children: ReactNode }> = ({ children }) => (
  <UIProvider>{children}</UIProvider>
);
